import React, { useEffect, useMemo, useState } from 'react';
import { FaLeaf, FaUtensils } from 'react-icons/fa';
import { isOrganicOnlySpecialtyPlan } from '../../data/specialtyMealPlans';
import { REGION_LABELS } from '../../data/specialtyCatalogApi';
import WeeklyMenuTable from './WeeklyMenuTable';
import { WEEKLY_MENU_INDICATIVE_NOTE } from './weeklyMenuNotes';
import './SpecialtyPlans.css';

export default function SpecialtyPlanDetailModal({
  plan,
  onClose,
  loadingMenu = false,
}) {
  const isOrganicOnly = isOrganicOnlySpecialtyPlan(plan);
  const [useOrganicPricing, setUseOrganicPricing] = useState(() => isOrganicOnlySpecialtyPlan(plan));
  const [regionKey, setRegionKey] = useState('south');

  const regionKeys = useMemo(() => {
    if (plan?.regionalMenus) return Object.keys(plan.regionalMenus);
    return [];
  }, [plan]);

  useEffect(() => {
    setUseOrganicPricing(isOrganicOnlySpecialtyPlan(plan));
    setRegionKey(regionKeys[0] || 'south');
  }, [plan?.id, regionKeys]);

  const supportsRegion = Boolean(plan?.regionalMenus && regionKeys.length);

  const displayPlan = useMemo(() => {
    if (!plan) return null;
    if (!supportsRegion) return plan;
    const region = plan.regionalMenus[regionKey] || plan.regionalMenus[regionKeys[0]];
    return {
      ...plan,
      weeklyMenu: region?.weeklyMenu || plan.weeklyMenu,
      activeRegionLabel: region?.label || REGION_LABELS[regionKey],
    };
  }, [plan, supportsRegion, regionKey, regionKeys]);

  const regionOptions = supportsRegion
    ? regionKeys.map((key) => ({
      key,
      label: plan.regionalMenus[key]?.label || REGION_LABELS[key] || key,
    }))
    : null;

  if (!plan || !displayPlan) return null;

  const hasMenu = Boolean(displayPlan.weeklyMenu?.length);

  return (
    <div
      className="mp-product-modal-overlay"
      role="presentation"
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div
        className={`mp-product-modal sp-specialty-detail-modal sp-specialty-detail-modal--${useOrganicPricing ? 'organic' : 'regular'}`}
        role="dialog"
        aria-labelledby="sp-specialty-detail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mp-product-modal__header sp-specialty-detail-modal__header">
          <div className="sp-specialty-detail-modal__title">
            <h3 id="sp-specialty-detail-title">{displayPlan.name}</h3>
            <p>
              {displayPlan.activeRegionLabel
                ? `${displayPlan.activeRegionLabel} · ${displayPlan.tagline}`
                : displayPlan.tagline}
            </p>
          </div>
          <div className={`sp-specialty-detail-modal__meal-type${isOrganicOnly ? ' sp-specialty-detail-modal__meal-type--organic-only' : ''}`} role="group" aria-label="Meal type">
            {!isOrganicOnly ? (
              <button
                type="button"
                className={!useOrganicPricing ? 'sp-detail-meal-type-btn sp-detail-meal-type-btn--regular sp-detail-meal-type-btn--active' : 'sp-detail-meal-type-btn sp-detail-meal-type-btn--regular'}
                onClick={() => setUseOrganicPricing(false)}
                aria-label="Regular meals"
                aria-pressed={!useOrganicPricing}
              >
                <FaUtensils aria-hidden />
                <span>Regular</span>
              </button>
            ) : null}
            <button
              type="button"
              className={(useOrganicPricing || isOrganicOnly) ? 'sp-detail-meal-type-btn sp-detail-meal-type-btn--active sp-detail-meal-type-btn--organic' : 'sp-detail-meal-type-btn sp-detail-meal-type-btn--organic'}
              onClick={() => setUseOrganicPricing(true)}
              aria-label="Organic meals"
              aria-pressed={useOrganicPricing || isOrganicOnly}
            >
              <FaLeaf aria-hidden />
              <span>Organic</span>
            </button>
          </div>
          <button type="button" className="mp-product-modal__close" onClick={onClose} aria-label="Close">
            x
          </button>
        </div>

        <div className="sp-specialty-detail-modal__body">
          {regionOptions?.length ? (
            <div className="sp-specialty-detail-modal__regions" role="tablist" aria-label="Regional menu">
              {regionOptions.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={regionKey === key}
                  className={regionKey === key ? 'sp-select-region-pill sp-select-region-pill--active' : 'sp-select-region-pill'}
                  onClick={() => setRegionKey(key)}
                >
                  {String(label).replace(' Meal', '')}
                </button>
              ))}
            </div>
          ) : null}

          <p className="sp-weekly-menu-indicative-note" role="note">
            <strong>Please note:</strong> {WEEKLY_MENU_INDICATIVE_NOTE}
          </p>

          {loadingMenu && !hasMenu ? (
            <p className="sp-muted" role="status">Loading weekly menu…</p>
          ) : (
            <WeeklyMenuTable
              plan={displayPlan}
              orientation="byDay"
              useOrganic={useOrganicPricing}
            />
          )}
        </div>
      </div>
    </div>
  );
}
